/* ===========================================================
   SaniZone — shared interactions
=========================================================== */

// Init everything after layout is injected
function ready(fn) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
  else fn();
}

// --- Global utilities ---
window.SH = window.SH || {};
// Title-case a string (for alt-text accessibility: Versa data is ALL CAPS).
window.SH.titleCase = function(str) {
  if (!str) return '';
  const keep = new Set(['en','in','op','de','het','een','van','voor','met','aan','bij','of','over','uit','als','naar','door']);
  return str.toLowerCase().replace(/[^\s-]+/g, (w, i) =>
    (i === 0 || !keep.has(w)) ? w[0].toUpperCase() + w.slice(1) : w
  );
};

// --- Recent viewed tracker (localStorage, max 12) ---
window.SH.RecentViewed = {
  KEY: 'sh_recent_viewed',
  MAX: 12,
  track(product) {
    if (!product || !product.handle) return;
    const payload = { handle: product.handle, title: product.title, price: product.price, image: product.image, brand: product.brand, compare_at: product.compare_at || null, collection: product.collection, at: Date.now() };
    let list = [];
    try { list = JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch {}
    list = list.filter(i => i.handle !== product.handle);
    list.unshift(payload);
    list = list.slice(0, this.MAX);
    try { localStorage.setItem(this.KEY, JSON.stringify(list)); } catch {}
  },
  get() {
    try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch { return []; }
  },
};

// --- Wishlist (localStorage) ---
window.SH.Wishlist = {
  KEY: 'sh_wishlist',
  get() {
    try { return JSON.parse(localStorage.getItem(this.KEY) || '[]'); } catch { return []; }
  },
  has(handle) { return this.get().some(i => i.handle === handle); },
  toggle(product) {
    let list = this.get();
    const exists = list.find(i => i.handle === product.handle);
    if (exists) list = list.filter(i => i.handle !== product.handle);
    else list.unshift({ handle: product.handle, title: product.title, price: product.price, image: product.image, brand: product.brand, compare_at: product.compare_at || null, at: Date.now() });
    try { localStorage.setItem(this.KEY, JSON.stringify(list)); } catch {}
    document.dispatchEvent(new CustomEvent('wishlist-change', { detail: { count: list.length } }));
    return !exists;
  },
  count() { return this.get().length; },
};

// --- Scroll reveal ---
function initReveal() {
  const targets = document.querySelectorAll('.reveal, .mask-reveal, .words');
  if (!('IntersectionObserver' in window)) {
    // Fallback: geen IO support → toon alles direct
    targets.forEach((el) => el.classList.add('in'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.05, rootMargin: '0px 0px -2% 0px' });
  targets.forEach((el) => io.observe(el));
  // Safety net: als IO om welke reden ook niet triggert binnen 4s, force visible
  // (voorkomt onzichtbare content bij browser-extensies of edge cases)
  setTimeout(() => {
    targets.forEach((el) => {
      if (!el.classList.contains('in')) el.classList.add('in');
    });
  }, 4000);
}

// --- Progress bar ---
function initProgressBar() {
  const bar = document.createElement('div');
  bar.className = 'progress-bar';
  document.body.prepend(bar);
  const update = () => {
    const h = document.documentElement;
    const scrolled = h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight);
    bar.style.transform = `scaleX(${scrolled})`;
  };
  document.addEventListener('scroll', update, { passive: true });
  update();
}

// --- Drawer / modal controls + focus-trap (H24) ---
function initOverlays() {
  const backdrop = () => document.getElementById('drawer-backdrop');
  const mobileMenu = () => document.getElementById('mobile-menu');
  const cart = () => document.getElementById('cart-drawer');
  const search = () => document.getElementById('search-modal');
  const triggers = { 'mobile-menu': 'mobile-menu-trigger', cart: 'cart-trigger' };

  let activeRelease = null;
  let lastTrigger = null;

  function setExpanded(type, val) {
    const id = triggers[type];
    if (id) document.getElementById(id)?.setAttribute('aria-expanded', val ? 'true' : 'false');
  }

  function activateModal(el) {
    if (!el) return;
    el.removeAttribute('aria-hidden');
    el.removeAttribute('inert');
    setTimeout(() => {
      const first = el.querySelector('input:not([type="hidden"]), button:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])');
      first?.focus({ preventScroll: true });
    }, 60);
  }
  function deactivateModal(el) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('inert', '');
  }

  function open(type) {
    lastTrigger = document.activeElement;
    if (type === 'mobile-menu') {
      const el = mobileMenu();
      el?.classList.add('open');
      document.body.classList.add('no-scroll');
      activateModal(el);
      if (el && window.SH?.focusTrap) activeRelease = window.SH.focusTrap(el, () => close(type));
    } else if (type === 'cart') {
      const el = cart();
      el?.classList.add('open');
      backdrop()?.classList.add('open');
      document.body.classList.add('no-scroll');
      activateModal(el);
      if (el && window.SH?.focusTrap) activeRelease = window.SH.focusTrap(el, () => close(type));
      try { if (window.Analytics && Cart && Cart.items && Cart.items.length) Analytics.viewCart(Cart.items); } catch (_) {}
    } else if (type === 'search') {
      const el = search();
      el?.classList.add('open');
      document.body.classList.add('no-scroll');
      el?.removeAttribute('aria-hidden');
      el?.removeAttribute('inert');
      setTimeout(() => document.getElementById('search-input')?.focus({ preventScroll: true }), 60);
      if (el && window.SH?.focusTrap) activeRelease = window.SH.focusTrap(el, () => close(type));
    }
    setExpanded(type, true);
  }
  function close(type) {
    if (type === 'mobile-menu' || !type) { const m = mobileMenu(); m?.classList.remove('open'); deactivateModal(m); setExpanded('mobile-menu', false); }
    if (type === 'cart' || !type) {
      const c = cart();
      c?.classList.remove('open');
      deactivateModal(c);
      backdrop()?.classList.remove('open');
      setExpanded('cart', false);
    }
    if (type === 'search' || !type) { const s = search(); s?.classList.remove('open'); deactivateModal(s); }
    document.body.classList.remove('no-scroll');
    if (activeRelease) { activeRelease(); activeRelease = null; }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      try { lastTrigger.focus({ preventScroll: true }); } catch (_) {}
    }
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-open]');
    if (openBtn) { open(openBtn.dataset.open); return; }
    const closeBtn = e.target.closest('[data-close]');
    if (closeBtn) { close(closeBtn.dataset.close); return; }
    if (e.target.id === 'drawer-backdrop') { close(); return; }
    // Click-outside-to-close voor search-modal (geen aparte backdrop)
    if (e.target.id === 'search-modal') { close('search'); return; }
    // Click-outside voor mobile-menu (klik buiten interne content)
    if (e.target.id === 'mobile-menu') { close('mobile-menu'); return; }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); open('search'); }
  });
}

// --- Shared escapeHtml utility (gebruikt door search + newsletter + render) ---
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// --- Newsletter form — POST to /api/newsletter ---
const AI_BACKEND = (function () {
  const h = location.hostname;
  if (h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local')) {
    return 'http://localhost:3007';
  }
  return 'https://ai.sanizone.nl';
})();

function initNewsletter() {
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-newsletter]');
    if (!form || form.dataset.shState === 'submitting' || form.dataset.shState === 'done') return;
    e.preventDefault();

    const input = form.querySelector('input[type="email"]');
    const email = input ? String(input.value || '').trim() : '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      form.querySelector('input[type="email"]')?.focus();
      return;
    }
    const honeypot = form.querySelector('input[name="website"]');
    const source = form.dataset.source || form.getAttribute('data-source') || 'footer';

    form.dataset.shState = 'submitting';
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const r = await fetch(`${AI_BACKEND}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, website: honeypot ? honeypot.value : '' }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        form.dataset.shState = 'error';
        if (submitBtn) submitBtn.disabled = false;
        const msg = r.status === 429
          ? 'Even rustig — probeer over een minuut opnieuw.'
          : 'Er ging iets mis. Probeer opnieuw of stuur ons een mail.';
        form.innerHTML = `<p role="alert" class="px-4 py-2 text-[14px] text-[color:var(--clay)] w-full text-center">${escapeHtml(msg)}</p>`;
        return;
      }
      form.dataset.shState = 'done';
      form.innerHTML = `<p role="status" class="px-4 py-2 text-[14px] text-[color:var(--oak)] w-full text-center">✦ Bedankt — je stijlgids komt eraan.</p>`;
    } catch (err) {
      form.dataset.shState = 'error';
      if (submitBtn) submitBtn.disabled = false;
      form.innerHTML = `<p role="alert" class="px-4 py-2 text-[14px] text-[color:var(--clay)] w-full text-center">Geen verbinding. Probeer opnieuw.</p>`;
    }
  });
}

// --- Contact form — POST to /api/contact ---
function initContactForm() {
  const form = document.querySelector('[data-contact-form]');
  if (!form) return;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (form.dataset.shState === 'submitting' || form.dataset.shState === 'done') return;

    const f = new FormData(form);
    const payload = {
      name: String(f.get('name') || '').trim(),
      email: String(f.get('email') || '').trim(),
      phone: String(f.get('phone') || '').trim(),
      message: String(f.get('message') || '').trim(),
      consent: !!f.get('consent'),
      website: String(f.get('website') || ''),
    };

    if (!payload.name || payload.name.length < 2) {
      form.querySelector('input[name="name"]')?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
      form.querySelector('input[name="email"]')?.focus();
      return;
    }
    if (!payload.message || payload.message.length < 10) {
      form.querySelector('textarea[name="message"]')?.focus();
      return;
    }
    if (!payload.consent) {
      form.querySelector('input[name="consent"]')?.focus();
      return;
    }

    form.dataset.shState = 'submitting';
    const submitBtn = form.querySelector('button[type="submit"]');
    const oldText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Versturen…'; }

    try {
      const r = await fetch(`${AI_BACKEND}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) {
        form.dataset.shState = 'error';
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = oldText; }
        form.innerHTML = `<p role="alert" class="px-4 py-3 text-[15px] text-[color:var(--clay)]">Er ging iets mis. Probeer opnieuw of mail direct naar info@sanizone.nl.</p>`;
        return;
      }
      form.dataset.shState = 'done';
      form.innerHTML = `<p role="status" class="px-4 py-3 text-[15px] text-[color:var(--moss)]">✦ Bedankt — we reageren binnen één werkdag.</p>`;
    } catch (err) {
      form.dataset.shState = 'error';
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = oldText; }
      form.innerHTML = `<p role="alert" class="px-4 py-3 text-[15px] text-[color:var(--clay)]">Geen verbinding. Probeer opnieuw of mail direct naar info@sanizone.nl.</p>`;
    }
  });
}

// --- Cross-sell map: what to suggest when cart contains item from X collection ---
const CROSS_SELL = {
  kranen: 'wastafels', wastafels: 'kranen', badkamermeubels: 'spiegels',
  douche: 'kranen', toiletten: 'accessoires', radiatoren: 'spiegels',
  spiegels: 'accessoires', accessoires: 'kranen', baden: 'kranen'
};
let _PRODUCTS_CACHE = null;
async function loadProducts() {
  if (_PRODUCTS_CACHE) return _PRODUCTS_CACHE;
  try {
    const r = await fetch('/data/products.json');
    _PRODUCTS_CACHE = await r.json();
  } catch { _PRODUCTS_CACHE = {}; }
  return _PRODUCTS_CACHE;
}
async function getCrossSells(cartHandles) {
  const data = await loadProducts();
  // Find first cart item's collection
  let srcColl = null;
  for (const [coll, items] of Object.entries(data)) {
    if (items.some(p => cartHandles.includes(p.handle))) { srcColl = coll; break; }
  }
  if (!srcColl) return [];
  const targetColl = CROSS_SELL[srcColl] || 'accessoires';
  const pool = (data[targetColl] || []).filter(p => !cartHandles.includes(p.handle));
  pool.sort((a,b) => {
    const ra = a.featured_rank || 3, rb = b.featured_rank || 3;
    if (ra !== rb) return ra - rb;
    return (b.price || 0) - (a.price || 0);
  });
  return pool.slice(0, 2);
}

// --- Cart — Shopify cart-API integration ---
const Cart = {
  items: [],
  total_price: 0, // in cents
  item_count: 0,
  refresh() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        this.items = (cart.items || []).map(i => ({
          key: i.key,
          handle: i.handle,
          title: i.product_title || i.title,
          variant_id: i.variant_id,
          variant_title: i.variant_title,
          qty: i.quantity,
          price: i.price / 100,
          compare_at: i.original_price && i.original_price > i.price ? i.original_price / 100 : null,
          line_price: i.final_line_price / 100,
          image: i.image,
          brand: i.vendor,
          url: i.url,
        }));
        this.total_price = (cart.total_price || 0) / 100;
        this.item_count = cart.item_count || 0;
        this.render();
        document.dispatchEvent(new Event('cart-change'));
        return cart;
      })
      .catch(err => { console.error('[Cart] refresh failed:', err); });
  },
  add(product) {
    const qty = product.qty || product.quantity || 1;
    const variantId = product.variant_id || product.id;
    const fetchVid = variantId
      ? Promise.resolve(variantId)
      : fetch(`/products/${product.handle}.js`).then(r => r.json()).then(p => p.variants[0].id);
    return fetchVid
      .then(vid => fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: vid, quantity: qty })
      }))
      .then(r => r.json())
      .then(line => {
        if (line.status >= 400) throw new Error(line.description || 'Add to cart failed');
        try { window.Analytics && Analytics.addToCart(product, qty); } catch (e) {}
        return this.refresh();
      })
      .catch(err => { console.error('[Cart] add failed:', err); });
  },
  remove(keyOrHandle) {
    const line = this.items.find(i => i.key === keyOrHandle) || this.items.find(i => i.handle === keyOrHandle);
    if (!line) return Promise.resolve();
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: line.key, quantity: 0 })
    })
      .then(r => r.json())
      .then(() => {
        try { window.Analytics && Analytics.removeFromCart(line, line.qty || 1); } catch (e) {}
        return this.refresh();
      });
  },
  updateQty(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(r => r.json()).then(() => this.refresh());
  },
  count() { return this.item_count; },
  total() { return this.total_price; },
  render() {
    const n = this.count();
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = n;
      el.classList.toggle('hidden', n === 0);
    });
    const trigger = document.getElementById('cart-trigger');
    if (trigger) trigger.setAttribute('aria-label', `Winkelmand · ${n} ${n === 1 ? 'item' : 'items'}`);
    const container = document.getElementById('cart-items');
    if (!container) return;
    if (this.items.length === 0) {
      container.innerHTML = `<div class="text-center py-20 text-[color:var(--muted)]">
        <svg class="w-12 h-12 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
        <p class="text-[15px]">Jouw winkelmand is leeg.</p>
        <a href="/collections/" class="mt-6 inline-block cta-pill">Bekijk assortiment <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></a>
      </div>`;
      const cb = document.getElementById('cart-checkout-bar');
      if (cb) { cb.hidden = true; cb.innerHTML = ''; }
      return;
    }
    const total = this.total();
    const SHIP_FREE = 75;
    const SHIP_MID = 50;
    const pct = Math.min(100, Math.round((total / SHIP_FREE) * 100));
    let shipMsg;
    if (total >= SHIP_FREE) {
      shipMsg = `<span class="flex items-center gap-1.5 text-[color:var(--moss)] font-medium"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>Je bestelling is <strong>gratis verzonden</strong>.</span>`;
    } else if (total >= SHIP_MID) {
      const remainingFree = SHIP_FREE - total;
      shipMsg = `Verzendkosten <strong>€4,95</strong> · nog <strong class="font-semibold text-ink">€${remainingFree.toFixed(0)}</strong> tot <strong>gratis verzending</strong>.`;
    } else {
      const remainingMid = SHIP_MID - total;
      shipMsg = `Verzendkosten <strong>€7,95</strong> · nog <strong class="font-semibold text-ink">€${remainingMid.toFixed(0)}</strong> tot <strong>€4,95</strong>.`;
    }

    container.innerHTML = `
      <div class="mb-6 p-4 rounded-2xl bg-[color:var(--paper)]">
        <div class="flex items-center justify-between text-[12px] mb-2 text-[color:var(--ink-soft)]">${shipMsg}</div>
        <div class="h-1.5 rounded-full bg-[rgba(26,26,26,0.08)] overflow-hidden">
          <div class="h-full bg-[color:var(--moss)] transition-all duration-500" style="width:${pct}%"></div>
        </div>
      </div>
    ` + this.items.map(i => `
      <div class="flex gap-4 py-5 border-b border-[color:var(--line)]">
        <div class="w-20 h-24 rounded-lg bg-[color:var(--paper)] overflow-hidden flex items-center justify-center shrink-0">
          <img src="${i.image}" alt="${SH.titleCase(i.title)}" class="w-full h-full object-contain p-2" loading="lazy" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-display text-[16px] display-tight text-ink line-clamp-2">${i.title}</p>
          <p class="text-[12px] text-[color:var(--muted)] mt-1">${i.brand}</p>
          <div class="flex items-center justify-between mt-3">
            <span class="text-[14px] font-medium">
              ${i.compare_at && i.compare_at > i.price
                ? `<span class="text-[color:var(--muted)] line-through mr-1.5 text-[12px]">€${(i.qty * i.compare_at).toFixed(2)}</span>`
                : ''}
              €${(i.qty * i.price).toFixed(2)}
            </span>
            <button data-cart-remove="${i.handle}" class="min-h-[44px] px-2 text-[12px] text-[color:var(--muted)] hover:text-[color:var(--clay)] transition">Verwijder</button>
          </div>
        </div>
      </div>
    `).join('') + `
      <div id="cart-xsell" class="mt-6"></div>
      <div class="mt-6 p-4 rounded-xl bg-[color:var(--paper)] border border-[color:var(--line)] relative overflow-hidden">
        <div class="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[color:var(--moss)] to-[color:var(--sage)] opacity-60"></div>
        <div class="flex items-start gap-3">
          <span class="ld-partner-badge shrink-0 mt-0.5">Powered by LeidingDeal</span>
        </div>
        <p class="mt-2 text-[13px] leading-[1.55] text-[color:var(--ink-soft)]">
          Installatiemateriaal erbij? Gebruik code <strong class="font-semibold text-ink tracking-wide">KLUSBUNDEL10</strong> bij <a href="https://leidingdeal.nl/?utm_source=sanizone&utm_medium=cross-sell&utm_campaign=cart-bundel" target="_blank" rel="noopener" class="link-draw text-ink">LeidingDeal</a> voor 10% korting.
        </p>
      </div>
    `;
    // Sticky footer met subtotaal + checkout-knop — altijd zichtbaar
    const checkoutBar = document.getElementById('cart-checkout-bar');
    if (checkoutBar) {
      checkoutBar.hidden = false;
      checkoutBar.innerHTML = `
        <div class="flex items-center justify-between mb-3">
          <span class="text-[14px] text-[color:var(--muted)]">Subtotaal</span>
          <span class="font-display text-[20px] display-tight">€${total.toFixed(2)}</span>
        </div>
        <a href="/checkout" data-checkout-cta class="cta-pill w-full justify-center min-h-[52px]">
          Naar afrekenen
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </a>
        <p class="mt-2 flex items-center gap-1.5 text-[11px] text-[color:var(--muted)] justify-center">
          <svg class="w-3 h-3 text-[color:var(--moss)]" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 2 4 6v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V6l-8-4z"/></svg>
          iDEAL · Klarna · Apple Pay
        </p>
      `;
    }
    // Async cross-sells
    const handles = this.items.map(i => i.handle);
    getCrossSells(handles).then(picks => {
      const slot = document.getElementById('cart-xsell');
      if (!slot || !picks.length) return;
      slot.innerHTML = `
        <p class="tracking-editorial text-[color:var(--muted)] text-[11px] mb-3">Vaak samen besteld</p>
        <div class="grid grid-cols-2 gap-3">
          ${picks.map(p => `
            <div class="relative rounded-xl bg-[color:var(--paper)] overflow-hidden">
              <a href="/products/?handle=${p.handle}" class="block aspect-[4/5] p-4 flex items-center justify-center">
                <img src="${p.image}" alt="${SH.titleCase(p.title)}" class="w-full h-full object-contain" loading="lazy" />
              </a>
              <div class="px-3 pb-3">
                <p class="text-[11px] text-[color:var(--muted)] truncate">${p.brand||''}</p>
                <p class="text-[12px] font-medium text-ink line-clamp-2 leading-tight mt-0.5">${p.title}</p>
                <div class="flex items-center justify-between mt-2">
                  <span class="text-[13px] font-medium">€${p.price.toFixed(0)}</span>
                  <button data-add-to-cart='${JSON.stringify({handle:p.handle,title:p.title,price:p.price,compare_at:p.compare_at||null,image:p.image,brand:p.brand}).replace(/'/g,"&apos;")}' class="w-7 h-7 rounded-full bg-ink text-bone flex items-center justify-center hover:bg-[color:var(--moss)] transition" aria-label="Toevoegen">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.4" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
                  </button>
                </div>
              </div>
            </div>
          `).join('')}
        </div>`;
    });
  },
};

// GA4 begin_checkout — fires op elke "Naar afrekenen" knop (cart drawer + cart page)
document.addEventListener('click', (e) => {
  const co = e.target.closest('[data-checkout-cta]');
  if (co) {
    try {
      if (window.Analytics && Cart && Cart.items) Analytics.beginCheckout(Cart.items);
    } catch (err) { /* niet blokkerend voor checkout-flow */ }
  }
}, true);

document.addEventListener('click', (e) => {
  const add = e.target.closest('[data-add-to-cart]');
  if (add) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const prod = JSON.parse(add.dataset.addToCart);
      // Visual feedback on quick-add button
      const icon = add.querySelector('.qa-icon');
      const check = add.querySelector('.qa-check');
      const label = add.querySelectorAll('.qa-label');
      if (icon && check) { icon.classList.add('hidden'); check.classList.remove('hidden'); }
      label.forEach(l => l.textContent = 'Toegevoegd');
      add.disabled = true;
      Cart.add(prod).finally(() => {
        setTimeout(() => {
          if (icon && check) { icon.classList.remove('hidden'); check.classList.add('hidden'); }
          label.forEach(l => l.textContent = 'Toevoegen');
          add.disabled = false;
        }, 1400);
      });
      // Open cart drawer
      document.getElementById('cart-drawer')?.classList.add('open');
      document.getElementById('drawer-backdrop')?.classList.add('open');
      document.body.classList.add('no-scroll');
    } catch (err) { console.error(err); }
  }
  const rem = e.target.closest('[data-cart-remove]');
  if (rem) {
    e.preventDefault();
    const onCartPage = !!document.querySelector('.cart-form');
    if (onCartPage) {
      const key = rem.dataset.cartRemove;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      }).then(() => window.location.reload());
    } else {
      Cart.remove(rem.dataset.cartRemove);
    }
  }
  const dec = e.target.closest('[data-cart-qty-decrease]');
  if (dec) {
    e.preventDefault();
    const key = dec.dataset.cartQtyDecrease;
    const input = dec.parentElement.querySelector('input[name^="updates"]');
    const newQty = Math.max(0, (parseInt(input.value, 10) || 1) - 1);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty }),
    }).then(() => window.location.reload());
  }
  const inc = e.target.closest('[data-cart-qty-increase]');
  if (inc) {
    e.preventDefault();
    const key = inc.dataset.cartQtyIncrease;
    const input = inc.parentElement.querySelector('input[name^="updates"]');
    const newQty = (parseInt(input.value, 10) || 1) + 1;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty }),
    }).then(() => window.location.reload());
  }
});

// --- Search (simple client-side) ---
// Synoniem-map: zoekterm → canonieke term (Fase 6.2)
const SEARCH_SYNONYMS = {
  'douchekraan':'douche','douchekop':'douche','regendouche':'douche','handdouche':'douche','doucheset':'douche','thermostaat':'douche','stortdouche':'douche','douchegoot':'douche',
  'wastafelkast':'badkamermeubels','wastafelmeubel':'badkamermeubels','badmeubel':'badkamermeubels','meubel':'badkamermeubels','kast':'badkamermeubels','onderkast':'badkamermeubels','kolomkast':'badkamermeubels',
  'wasbak':'wastafels','fontein':'wastafels','waskom':'wastafels','opzetkom':'wastafels','wastafel':'wastafels',
  'wc':'toiletten','toilet':'toiletten','hangtoilet':'toiletten','staand toilet':'toiletten','smart toilet':'toiletten','bidet':'toiletten',
  'badkuip':'baden','vrijstaand':'baden','ligbad':'baden','whirlpool':'baden','jacuzzi':'baden','inbouwbad':'baden',
  'spiegel':'spiegels','led spiegel':'spiegels','badkamerspiegel':'spiegels','ronde spiegel':'spiegels',
  'radiator':'radiatoren','handdoekradiator':'radiatoren','designradiator':'radiatoren','verwarming':'radiatoren',
  'haak':'accessoires','handdoekhouder':'accessoires','planchet':'accessoires','zeepdispenser':'accessoires','handdoekring':'accessoires','closetborstel':'accessoires','toiletrolhouder':'accessoires',
  'kraan':'kranen','mengkraan':'kranen','wastafelkraan':'kranen','badkraan':'kranen','koudwaterkraan':'kranen','muurkraan':'kranen',
  'zwart':'mat zwart','zwarte':'mat zwart','goud':'geborsteld goud','gouden':'geborsteld goud','messing':'geborsteld goud','koper':'geborsteld koper','grijs':'gun metal','brons':'brons','chroom':'chroom',
};

const COLLECTION_LABELS = {
  kranen:'Kranen', wastafels:'Wastafels', badkamermeubels:'Meubels', douche:'Douche',
  toiletten:'Toiletten', spiegels:'Spiegels', radiatoren:'Radiatoren', accessoires:'Accessoires', baden:'Baden'
};

function expandQuery(q) {
  const tokens = q.split(/\s+/).filter(Boolean);
  const expanded = new Set(tokens);
  const suggestedCollection = new Set();
  for (const t of tokens) {
    const syn = SEARCH_SYNONYMS[t];
    if (syn) {
      expanded.add(syn);
      if (COLLECTION_LABELS[syn]) suggestedCollection.add(syn);
    }
  }
  // Multi-word synoniemen (bv. "smart toilet")
  for (const [k, v] of Object.entries(SEARCH_SYNONYMS)) {
    if (k.includes(' ') && q.includes(k)) {
      expanded.add(v);
      if (COLLECTION_LABELS[v]) suggestedCollection.add(v);
    }
  }
  return { tokens: [...expanded], collections: [...suggestedCollection] };
}

async function initSearch() {
  const input = document.getElementById('search-input');
  if (!input) return;
  let products = [];
  let subcols = [];
  try {
    const [pRes, sRes] = await Promise.all([
      fetch('/data/products-flat.json'),
      fetch('/data/subcollections.json').catch(() => null),
    ]);
    products = await pRes.json();
    if (sRes && sRes.ok) {
      const data = await sRes.json();
      for (const col of data) {
        for (const sub of col.subs) {
          if (sub.slug === 'overig') continue;
          subcols.push({
            title: sub.title,
            count: sub.count,
            href: `/collections/${col.parent}/${sub.slug}/`,
            search: (sub.title + ' ' + (col.parent_title || col.parent)).toLowerCase(),
          });
        }
      }
    }
  } catch { return; }

  const resultsBox = document.getElementById('search-results');
  let timer;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const q = input.value.toLowerCase().trim();
      if (q.length < 2) {
        resultsBox.innerHTML = `<p class="tracking-editorial text-[color:var(--muted)] mb-6">Populair</p>
          <div class="flex flex-wrap gap-3">
            <a href="/collections/kranen/" class="px-5 py-2.5 rounded-full bg-[color:var(--paper)] hover:bg-[color:var(--oak)] hover:text-ink transition text-[14px]">Mengkranen</a>
            <a href="/collections/badkamermeubels/" class="px-5 py-2.5 rounded-full bg-[color:var(--paper)] hover:bg-[color:var(--oak)] hover:text-ink transition text-[14px]">Wastafelmeubels</a>
            <a href="/collections/spiegels/" class="px-5 py-2.5 rounded-full bg-[color:var(--paper)] hover:bg-[color:var(--oak)] hover:text-ink transition text-[14px]">LED-spiegels</a>
            <a href="/collections/baden/" class="px-5 py-2.5 rounded-full bg-[color:var(--paper)] hover:bg-[color:var(--oak)] hover:text-ink transition text-[14px]">Vrijstaande baden</a>
            <a href="/collections/douche/" class="px-5 py-2.5 rounded-full bg-[color:var(--paper)] hover:bg-[color:var(--oak)] hover:text-ink transition text-[14px]">Regendouches</a>
            <a href="/studio/" class="px-5 py-2.5 rounded-full bg-[color:var(--ink)] text-bone hover:bg-[color:var(--moss)] transition text-[14px]">AI Studio</a>
          </div>`;
        return;
      }
      const { tokens, collections } = expandQuery(q);
      // Match: any token matches title or brand
      const matches = products.filter((p) => {
        const text = (p.title + ' ' + p.brand + ' ' + (p.tags || '') + ' ' + (p.collection || '')).toLowerCase();
        return tokens.some(t => text.includes(t));
      }).sort((a,b) => {
        // Sort by featured_rank then price desc
        const ra = a.featured_rank || 3, rb = b.featured_rank || 3;
        if (ra !== rb) return ra - rb;
        return (b.price || 0) - (a.price || 0);
      }).slice(0, 8);

      // "Bedoelde je ..." — tonen als zoekterm een synoniem matchte die niet letterlijk in de input stond
      const didYouMean = collections.filter(c => !q.includes(c)).slice(0, 2);
      const didYouMeanHtml = didYouMean.length ? `
        <p class="text-[12px] text-[color:var(--muted)] mb-4">Bedoelde je: ${didYouMean.map(c => `<a href="/collections/${c}/" class="link-draw text-ink font-medium">${COLLECTION_LABELS[c]}</a>`).join(' · ')}?</p>
      ` : '';

      // Match subcollecties op zoekterm
      const subMatches = subcols
        .map(s => ({ s, hits: tokens.filter(t => s.search.includes(t)).length }))
        .filter(x => x.hits > 0)
        .sort((a, b) => b.hits - a.hits || b.s.count - a.s.count)
        .slice(0, 4)
        .map(x => x.s);
      const subHtml = subMatches.length ? `
        <div class="mb-6 pb-5 border-b border-[color:var(--line)]">
          <p class="tracking-editorial text-[color:var(--muted)] mb-3">Categorieën</p>
          <div class="flex flex-wrap gap-2">
            ${subMatches.map(s => `<a href="${s.href}" class="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--line)] hover:border-ink hover:bg-ink hover:text-bone transition text-[13px]"><span>${s.title}</span><span class="text-[color:var(--muted)] opacity-70">${s.count}</span></a>`).join('')}
          </div>
        </div>` : '';

      if (!matches.length && !subMatches.length) {
        resultsBox.innerHTML = didYouMeanHtml + `<p class="text-[color:var(--muted)]">Geen resultaten voor "${escapeHtml(q)}".</p>`;
        return;
      }
      resultsBox.innerHTML = didYouMeanHtml + subHtml + (matches.length ? `
        <p class="tracking-editorial text-[color:var(--muted)] mb-6">${matches.length} resultaten</p>
        <div class="grid gap-3">
          ${matches.map((p) => `
            <a href="/products/${p.handle}/" class="flex items-center gap-4 md:gap-5 p-3 rounded-2xl hover:bg-[color:var(--paper)] transition group">
              <div class="w-20 h-24 md:w-24 md:h-28 rounded-xl bg-[color:var(--paper)] overflow-hidden flex items-center justify-center shrink-0">
                <img src="${p.image}" class="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105" alt="" loading="lazy" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[10.5px] tracking-[0.15em] uppercase text-[color:var(--muted)]">${p.brand}</p>
                <p class="font-display text-[17px] md:text-[19px] display-tight text-ink line-clamp-2 mt-1 leading-[1.2]">${p.title}</p>
                <p class="mt-1 text-[11px] text-[color:var(--moss)] tracking-[0.04em]">→ Bekijk product</p>
              </div>
              <span class="font-display text-[18px] text-ink shrink-0">€${p.price.toFixed(0)}</span>
            </a>
          `).join('')}
        </div>
      ` : '');
    }, 150);
  });
}

// --- Mouse parallax (optional, subtle) ---
function initParallax() {
  document.querySelectorAll('[data-parallax]').forEach((el) => {
    el.addEventListener('mousemove', (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const targets = el.querySelectorAll('[data-parallax-item]');
      targets.forEach((t) => {
        const f = parseFloat(t.dataset.parallaxFactor || '10');
        t.style.transform = `translate(${x * f}px, ${y * f}px)`;
      });
    });
    el.addEventListener('mouseleave', () => {
      el.querySelectorAll('[data-parallax-item]').forEach((t) => (t.style.transform = ''));
    });
  });
}

// --- A/B test variant-switcher (Fase 8.6) ---
// URL-param ?v=a of ?v=b wisselt elementen met data-variant-a/data-variant-b
function initABTest() {
  const params = new URLSearchParams(location.search);
  let variant = params.get('v');
  // Persist per session
  if (variant === 'a' || variant === 'b') {
    sessionStorage.setItem('sh_variant', variant);
  } else {
    variant = sessionStorage.getItem('sh_variant');
  }
  if (variant !== 'a' && variant !== 'b') return;
  document.body.setAttribute('data-variant', variant);
  // Swap data-variant-a / data-variant-b textContent on matching elements
  document.querySelectorAll('[data-variant-a]').forEach(el => {
    const alt = el.getAttribute('data-variant-' + variant);
    if (alt) el.textContent = alt;
  });
  // GA4 event if gtag available
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'ab_variant', { variant });
  }
}

// --- Header free-shipping progress (Fase 7.4) ---
function initHeaderShipProgress() {
  const container = document.getElementById('header-ship-progress');
  const bar = document.getElementById('header-ship-bar');
  if (!container || !bar) return;
  function update() {
    const items = JSON.parse(localStorage.getItem('sh_cart') || '[]');
    const total = items.reduce((s, i) => s + (i.qty || 1) * (i.price || 0), 0);
    if (total <= 0) { container.classList.add('hidden'); return; }
    container.classList.remove('hidden');
    const pct = Math.min(100, (total / 200) * 100);
    bar.style.width = pct + '%';
  }
  update();
  window.addEventListener('storage', update);
  document.addEventListener('cart-change', update);
  // Poll als fallback (localStorage events werken niet in dezelfde tab)
  setInterval(update, 2000);
}

// --- Floating "gratis verzending" badge (bottom-right, zodra er iets in cart zit) ---
function initShipFloater() {
  // Skip op PDP (sticky-bar wint), cart & checkout (al zichtbaar), wishlist
  if (/^\/(checkout|cart|wishlist)/.test(location.pathname)) return;
  if (location.pathname.startsWith('/products/')) return;
  const FREE = 200;
  const bar = document.createElement('div');
  bar.id = 'ship-floater';
  bar.className = 'fixed z-[45] bottom-4 right-4 md:bottom-6 md:right-6 max-w-[300px] bg-bone border border-[color:var(--line)] shadow-drop rounded-2xl px-4 py-3 pr-5 flex items-center gap-3 transition-all duration-500';
  bar.style.transform = 'translateY(200%)';
  bar.style.opacity = '0';
  bar.innerHTML = `
    <div class="shrink-0 w-9 h-9 rounded-full bg-[color:var(--paper)] flex items-center justify-center text-[color:var(--moss)]">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
    </div>
    <div class="flex-1 min-w-0">
      <p class="text-[11px] tracking-[0.1em] uppercase text-[color:var(--muted)] leading-none" data-ship-eyebrow>Nog €— tot gratis</p>
      <div class="mt-1.5 h-1 rounded-full bg-[rgba(26,26,26,0.08)] overflow-hidden"><div data-ship-bar class="h-full bg-[color:var(--moss)] transition-all duration-500" style="width:0%"></div></div>
    </div>
  `;
  document.body.appendChild(bar);
  const eyebrow = bar.querySelector('[data-ship-eyebrow]');
  const fill = bar.querySelector('[data-ship-bar]');
  function update() {
    const items = JSON.parse(localStorage.getItem('sh_cart') || '[]');
    const total = items.reduce((s,i)=> s + (i.qty||1)*(i.price||0), 0);
    if (total <= 0) { bar.style.transform = 'translateY(200%)'; bar.style.opacity = '0'; return; }
    bar.style.transform = 'translateY(0)'; bar.style.opacity = '1';
    const pct = Math.min(100, (total / FREE) * 100);
    fill.style.width = pct + '%';
    if (total >= FREE) {
      eyebrow.textContent = '✓ Gratis verzending unlocked';
      eyebrow.style.color = 'var(--moss)';
    } else {
      const remaining = FREE - total;
      eyebrow.textContent = `Nog €${remaining.toFixed(0)} tot gratis verzending`;
      eyebrow.style.color = '';
    }
  }
  update();
  document.addEventListener('cart-change', update);
  window.addEventListener('storage', update);
  setInterval(update, 2500);
}

// --- Blog CTA injection (Fase 9.1) — automatisch op elke /blogs/ pagina ---
function initBlogCTA() {
  if (!location.pathname.startsWith('/blogs/')) return;
  if (document.getElementById('blog-cta-injected')) return;
  const footerMount = document.getElementById('footer-mount');
  if (!footerMount) return;

  const cta = document.createElement('section');
  cta.id = 'blog-cta-injected';
  cta.className = 'relative py-16 md:py-24 bg-[color:var(--paper)] border-t border-[color:var(--line)]';
  cta.innerHTML = `
    <div class="max-w-[1180px] mx-auto px-6 md:px-10">
      <div class="max-w-[720px] mb-10 md:mb-14 reveal">
        <p class="tracking-editorial text-[color:var(--muted)] mb-3">Hulp bij je keuze</p>
        <h2 class="font-display display-tighter text-[32px] md:text-[44px] text-ink leading-[1.05]">
          Van inspiratie naar <em class="text-[color:var(--moss)] not-italic">jouw badkamer</em>.
        </h2>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <a href="/collections/" class="group p-6 md:p-7 rounded-[20px] bg-bone border border-[color:var(--line)] hover:shadow-lift transition">
          <div class="w-10 h-10 rounded-full bg-[color:var(--sage)]/25 flex items-center justify-center text-[color:var(--moss)] mb-4">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
          </div>
          <h3 class="font-display text-[18px] font-semibold text-ink">Bekijk de collectie</h3>
          <p class="mt-2 text-[14px] leading-[1.55] text-[color:var(--ink-soft)]">2.000+ gecureerde items. Morgen in huis voor 10:00 besteld.</p>
          <span class="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--moss)] group-hover:gap-2.5 transition-all">Naar collecties <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.4" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></span>
        </a>
        <a href="tel:+31641613498" class="group p-6 md:p-7 rounded-[20px] bg-bone border border-[color:var(--line)] hover:shadow-lift transition">
          <div class="w-10 h-10 rounded-full bg-[color:var(--sage)]/25 flex items-center justify-center text-[color:var(--moss)] mb-4">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          </div>
          <h3 class="font-display text-[18px] font-semibold text-ink">Persoonlijk advies</h3>
          <p class="mt-2 text-[14px] leading-[1.55] text-[color:var(--ink-soft)]">Bel of mail onze helden · antwoord binnen 24 uur van een echt mens. service@sani-zone.nl</p>
          <span class="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[color:var(--moss)] group-hover:gap-2.5 transition-all">Bel 06 41 61 34 98 <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.4" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></span>
        </a>
      </div>
    </div>`;
  footerMount.parentNode.insertBefore(cta, footerMount);
}

// --- Exit-intent popup (5% korting · 1x per 7 dagen) ---
function initExitIntent() {
  const KEY = 'sh_exitintent_dismissed_at';
  const COOLDOWN = 7 * 24 * 60 * 60 * 1000;
  try {
    const last = parseInt(localStorage.getItem(KEY) || '0', 10);
    if (Date.now() - last < COOLDOWN) return;
  } catch {}
  // Skip on checkout/cart/wishlist
  if (/^\/(checkout|cart|wishlist)/.test(location.pathname)) return;
  let triggered = false;
  const trigger = () => {
    if (triggered) return; triggered = true;
    const modal = document.createElement('div');
    modal.id = 'exit-intent';
    modal.className = 'fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-8';
    modal.style.background = 'rgba(26,26,26,0.55)';
    modal.style.backdropFilter = 'blur(4px)';
    modal.innerHTML = `
      <div class="relative max-w-[480px] w-full rounded-[24px] p-7 md:p-10 shadow-drop" style="background:#FAF7F2;isolation:isolate" role="dialog" aria-modal="true" aria-labelledby="exit-title">
        <button data-exit-close aria-label="Sluiten" class="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-[color:var(--paper)] flex items-center justify-center">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M6 6l12 12M6 18L18 6"/></svg>
        </button>
        <p class="tracking-editorial text-[color:var(--moss)] mb-3">Nog even voor je gaat</p>
        <h2 id="exit-title" class="font-display text-[24px] md:text-[32px] text-ink leading-[1.25] tracking-[-0.02em] font-medium">
          5% korting op je eerste bestelling.
        </h2>
        <p class="mt-4 text-[14px] md:text-[15px] leading-[1.65] text-[color:var(--ink-soft)]">
          Meld je aan voor de Stijlgids 2026 nieuwsbrief &mdash; je ontvangt direct een kortingscode en 1&times;/maand badkamer-inspiratie.
        </p>
        <form data-newsletter data-exit-form class="mt-5 flex gap-2 bg-[color:var(--paper)] rounded-full p-1.5 border border-[color:var(--line)]">
          <input type="email" required placeholder="jouw@email.nl" class="flex-1 bg-transparent px-4 py-2 text-[14px] focus:outline-none" />
          <button type="submit" class="px-5 py-2 bg-ink text-bone rounded-full text-[13px] font-medium hover:bg-[color:var(--moss)] transition">Inschrijven</button>
        </form>
        <p class="mt-3 text-[11px] text-[color:var(--muted)]">Geen spam. Afmelden in elke mail.</p>
      </div>
    `;
    document.body.appendChild(modal);
    const close = () => {
      try { localStorage.setItem(KEY, String(Date.now())); } catch {}
      modal.remove();
    };
    modal.querySelector('[data-exit-close]').onclick = close;
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', function esc(e) { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', esc); } });
  };
  // Desktop: mouse leaves viewport top
  document.addEventListener('mouseout', (e) => {
    if (!e.relatedTarget && e.clientY < 10) trigger();
  });
  // Mobile: fast upward scroll after 8 sec on-page
  let lastY = window.scrollY;
  const pageLoadAt = Date.now();
  window.addEventListener('scroll', () => {
    if (Date.now() - pageLoadAt < 8000) return;
    if (window.scrollY + 40 < lastY) trigger();
    lastY = window.scrollY;
  }, { passive: true });
}

// --- Recent viewed strip renderer ---
function renderRecentViewed() {
  const host = document.getElementById('recent-viewed');
  if (!host) return;
  const list = SH.RecentViewed.get();
  // Current PDP handle excluden
  const currentHandle = window.__PDP_HANDLE || null;
  const items = list.filter(i => i.handle !== currentHandle).slice(0, 6);
  if (!items.length) { host.hidden = true; return; }
  host.hidden = false;
  host.innerHTML = `
    <div class="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--line)]">
      <div class="flex items-end justify-between mb-8">
        <div>
          <p class="tracking-editorial text-[color:var(--muted)] mb-3">Recent bekeken</p>
          <h2 class="font-display display-tight text-[28px] md:text-[40px] text-ink leading-[1.1]">Je was hier net aan het kijken.</h2>
        </div>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-5">
        ${items.map(p => `
          <a href="/products/${p.handle}/" class="group block">
            <div class="relative rounded-[14px] bg-[color:var(--paper)] aspect-square overflow-hidden p-3 md:p-4">
              <img src="${p.image || ''}" alt="${SH.titleCase(p.title || '')}" class="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.04]" loading="lazy" />
              ${p.compare_at && p.compare_at > p.price ? `<span class="absolute top-2 left-2 px-2 py-0.5 bg-[color:var(--clay)] text-bone text-[9px] tracking-[0.14em] uppercase rounded-full">-${Math.round((1-p.price/p.compare_at)*100)}%</span>` : ''}
            </div>
            <p class="mt-2 text-[10px] tracking-editorial text-[color:var(--muted)]">${p.brand || ''}</p>
            <h3 class="text-[13px] font-medium text-ink leading-[1.35] line-clamp-2">${SH.titleCase(p.title || '')}</h3>
            <p class="mt-1 text-[13px] font-semibold text-ink">${p.price ? '€'+p.price.toFixed(0) : ''}</p>
          </a>
        `).join('')}
      </div>
    </div>
  `;
}

// --- Wishlist UI sync (header-badge + PDP heart button) ---
function initWishlist() {
  const updateBadge = () => {
    const count = SH.Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };
  document.addEventListener('wishlist-change', updateBadge);
  updateBadge();
  // PDP heart-toggle
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-wishlist-toggle]');
    if (!b) return;
    e.preventDefault();
    try {
      const p = JSON.parse(b.dataset.wishlistToggle);
      const added = SH.Wishlist.toggle(p);
      b.classList.toggle('is-wishlisted', added);
      b.setAttribute('aria-pressed', added ? 'true' : 'false');
    } catch {}
  });
  // Sync heart buttons on initial render
  document.querySelectorAll('[data-wishlist-toggle]').forEach(b => {
    try {
      const p = JSON.parse(b.dataset.wishlistToggle);
      if (SH.Wishlist.has(p.handle)) {
        b.classList.add('is-wishlisted');
        b.setAttribute('aria-pressed', 'true');
      }
    } catch {}
  });
}

// --- PDP image-lightbox (M3) ---
function initImageLightbox() {
  const targets = document.querySelectorAll('[data-lightbox], #pdp-main-img, #sticky-img');
  if (!targets.length) return;
  let modal = null;
  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.id = 'sh-lightbox';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Productfoto vergroot');
    modal.innerHTML = '<button type="button" aria-label="Sluit" data-lb-close>×</button><img alt="" />';
    Object.assign(modal.style, {
      position: 'fixed', inset: '0', display: 'none',
      background: 'rgba(26,26,26,0.92)', zIndex: '9998',
      alignItems: 'center', justifyContent: 'center', padding: '40px',
    });
    const img = modal.querySelector('img');
    Object.assign(img.style, {
      maxWidth: '95vw', maxHeight: '90vh', objectFit: 'contain',
      borderRadius: '8px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    });
    const btn = modal.querySelector('[data-lb-close]');
    Object.assign(btn.style, {
      position: 'absolute', top: '20px', right: '20px',
      width: '44px', height: '44px', borderRadius: '999px',
      background: '#FAF7F2', color: '#1A1A1A', border: '0',
      fontSize: '24px', lineHeight: '1', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    });
    modal.addEventListener('click', (e) => {
      if (e.target === modal || e.target.closest('[data-lb-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') close();
    });
    document.body.appendChild(modal);
    return modal;
  }
  function open(src, alt) {
    const m = ensureModal();
    const img = m.querySelector('img');
    img.src = src;
    img.alt = alt || '';
    m.style.display = 'flex';
    document.body.classList.add('no-scroll');
  }
  function close() {
    if (!modal) return;
    modal.style.display = 'none';
    document.body.classList.remove('no-scroll');
  }
  targets.forEach((el) => {
    el.style.cursor = 'zoom-in';
    el.addEventListener('click', (e) => {
      if (el.tagName !== 'IMG') return;
      e.preventDefault();
      open(el.currentSrc || el.src, el.alt);
    });
  });
}

// --- M9: 'X anderen kochten dit deze week' (v1, deterministic-pseudo) ---
// Toont count >=3 op PDPs met [data-others-bought], gebaseerd op handle-hash.
// v2: vervang door Shopify orders-count cache via Trigger.dev.
window.initOthersBought = function() {
  const slot = document.querySelector('[data-others-bought]');
  if (!slot) return;
  const handle = slot.getAttribute('data-others-bought') || (document.body && document.body.dataset.handle) || '';
  if (!handle) return;
  // deterministic hash → range 0..15 mapping
  let h = 0;
  for (let i = 0; i < handle.length; i++) h = ((h << 5) - h + handle.charCodeAt(i)) | 0;
  // 30-day rotating offset zodat het cijfer mee-pulseert
  const week = Math.floor(Date.now() / (1000 * 60 * 60 * 24 * 7));
  const count = (Math.abs(h) + week) % 13;
  if (count < 3) { slot.remove(); return; }
  slot.innerHTML = `<div class="flex items-center gap-2.5 text-[13px] text-[color:var(--ink-soft)] py-2">
    <span class="relative flex h-2 w-2"><span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--moss)] opacity-75"></span><span class="relative inline-flex rounded-full h-2 w-2 bg-[color:var(--moss)]"></span></span>
    <span><strong class="text-ink">${count}</strong> anderen kochten dit deze week</span>
  </div>`;
}

// --- Mega-menu uses CSS hover (.mega-trigger:hover .mega-panel) ---
function initMegaMenuAria() {
  // CSS handles show/hide; this is a no-op for compatibility
}

ready(() => {
  setTimeout(() => {
    initReveal();
    initProgressBar();
    initOverlays();
    initNewsletter();
    initContactForm();
    // Defer initSearch() until first user interaction to avoid loading products-flat.json on every page
    (function deferSearchInit() {
      const searchInput = document.getElementById('search-input');
      const searchTriggers = document.querySelectorAll('[data-open="search-modal"], [data-search-trigger]');
      let initialized = false;
      const triggerInit = () => {
        if (initialized) return;
        initialized = true;
        initSearch();
      };
      if (searchInput) searchInput.addEventListener('focus', triggerInit, { once: true });
      searchTriggers.forEach(t => t.addEventListener('click', triggerInit, { once: true }));
      // Fallback: init after 8s of idle in case user uses keyboard shortcut
      setTimeout(triggerInit, 8000);
    })();
    initParallax();
    initBlogCTA();
    initHeaderShipProgress();
    initABTest();
    initWishlist();
    renderRecentViewed();
    // initExitIntent();  // disabled — gebruiker vraagt geen popup voorlopig
    initShipFloater();
    initImageLightbox();
    initMegaMenuAria();
    window.initOthersBought();
    Cart.render();
    // Prefetch PDP HTML on product-card hover for instant click-through (desktop + slow-mobile)
    initHoverPrefetch();
  }, 10);
});

function initHoverPrefetch() {
  const prefetched = new Set();
  const prefetch = (url) => {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  };
  // On hover (desktop) — prefetch PDP HTML
  document.body.addEventListener('mouseover', (e) => {
    const card = e.target.closest('a.product-card[href*="/products/"]');
    if (card && card.href) prefetch(card.href);
  }, { passive: true });
  // On touchstart (mobile) — prefetch on tap-start (~100ms before click fires)
  document.body.addEventListener('touchstart', (e) => {
    const card = e.target.closest('a[href*="/products/"]');
    if (card && card.href) prefetch(card.href);
  }, { passive: true });
}

/* ==== A11y: Focus trap utility voor modals/drawers ==== */
(function() {
  if (typeof window === 'undefined') return;
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

  function trap(container, onEscape) {
    if (!container) return () => {};
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE)).filter(el => !el.hasAttribute('aria-hidden'));
    if (!focusables.length) return () => {};
    const first = focusables[0], last = focusables[focusables.length - 1];
    const prevFocus = document.activeElement;

    function onKey(e) {
      if (e.key === 'Escape' && onEscape) { e.stopPropagation(); onEscape(); return; }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    setTimeout(() => first.focus(), 20);
    return function release() {
      container.removeEventListener('keydown', onKey);
      if (prevFocus && prevFocus.focus) { try { prevFocus.focus(); } catch (_) {} }
    };
  }

  window.SH = window.SH || {};
  window.SH.focusTrap = trap;

  // Auto-apply op bekende drawers/modals: [data-modal-open] → [data-modal-target]
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (!opener) return;
    const target = document.querySelector(opener.dataset.modalOpen) || document.getElementById(opener.dataset.modalOpen);
    if (!target) return;
    target.setAttribute('aria-modal', 'true');
    target.setAttribute('role', target.getAttribute('role') || 'dialog');
    const release = trap(target, () => {
      target.classList.remove('open');
      release();
    });
  });
})();

/* ==== Canonical delivery-day helper (NL tijdzone, werkdagen-only) ==== */
/* Logic: order ships next werkdag (of vandaag voor 10:00 op werkdag), levering = ship + 1 werkdag.
   Saturday/Sunday → ship Monday → arrive Tuesday. Vrijdag voor 10 → ship Friday → arrive Monday. */
(function() {
  const DAYS_LOWER = ['zondag','maandag','dinsdag','woensdag','donderdag','vrijdag','zaterdag'];
  function getDeliveryDay() {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
    const d = now.getDay(), h = now.getHours();
    let shipOffset;
    if (d === 0)                shipOffset = 1;   // zondag → ma
    else if (d === 6)           shipOffset = 2;   // zaterdag → ma
    else if (d === 5 && h >= 10) shipOffset = 3;  // vrijdag na 10 → ma
    else if (h >= 10)           shipOffset = 1;   // ma-do na 10 → morgen
    else                        shipOffset = 0;   // werkdag voor 10 → vandaag
    const shipDate = new Date(now); shipDate.setDate(shipDate.getDate() + shipOffset);
    const shipDay = shipDate.getDay();
    const deliverOffset = (shipDay === 5) ? shipOffset + 3 : shipOffset + 1;
    const deliverDate = new Date(now); deliverDate.setDate(deliverDate.getDate() + deliverOffset);
    const dayIdx = deliverDate.getDay();
    const lower = DAYS_LOWER[dayIdx];
    return {
      day: dayIdx,
      lower,
      label: lower.charAt(0).toUpperCase() + lower.slice(1),
      labelInHuis: lower.charAt(0).toUpperCase() + lower.slice(1) + ' in huis',
      date: deliverDate,
      shipDate,
    };
  }
  window.SH = window.SH || {};
  window.SH.getDeliveryDay = getDeliveryDay;

  // Override delivery-text op PDP's en delivery-bar-label in header — fixt de oude buggy inline logic
  function updateAllDeliveryLabels() {
    const dd = getDeliveryDay();
    const months = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];
    const dateLabel = dd.lower + ' ' + dd.date.getDate() + ' ' + months[dd.date.getMonth()];
    // PDP delivery-text: "Voor 10:00 besteld · <day> in huis" (or weekend variant)
    const pdpEl = document.getElementById('delivery-text');
    if (pdpEl) {
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
      const isWeekend = now.getDay() === 0 || now.getDay() === 6;
      const before12 = now.getHours() < 10 && !isWeekend;
      if (isWeekend) {
        pdpEl.innerHTML = 'Bestel nu &middot; <strong>' + dd.lower + ' ' + dd.date.getDate() + ' ' + months[dd.date.getMonth()] + ' in huis</strong>';
      } else if (before12) {
        // Countdown wordt elders gezet — overschrijf alleen als die niet draait
        if (!pdpEl.dataset.countdownActive) {
          pdpEl.innerHTML = 'Voor 10:00 besteld &middot; <strong>' + dateLabel + ' in huis</strong>';
        }
      } else {
        pdpEl.innerHTML = 'Bestel nu &middot; <strong>' + dateLabel + ' in huis</strong>';
      }
    }
    // Header announcement bar
    const barEl = document.getElementById('delivery-bar-label');
    if (barEl) barEl.textContent = dd.lower + ' in huis';
    // Static PDP "Morgen in huis" trust-icon strip
    document.querySelectorAll('[data-trust-delivery]').forEach(el => { el.textContent = dd.label + ' in huis'; });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAllDeliveryLabels);
  } else {
    updateAllDeliveryLabels();
  }
  // Run again na 100ms en 1s om te overschrijven nadat inline-scripts hun ding deden
  setTimeout(updateAllDeliveryLabels, 150);
  setTimeout(updateAllDeliveryLabels, 1200);
})();

/* ──── Mobile filters drawer toggle ──────────────────────────────── */
(function () {
  function init() {
    var aside = document.getElementById('filters-aside');
    var openBtn = document.getElementById('mobile-filters-open');
    var openSortBtn = document.getElementById('mobile-filters-open-sort');
    var closeBtn = document.getElementById('mobile-filters-close');
    if (!aside || !openBtn) return;
    function open() {
      aside.classList.add('is-open');
      document.body.classList.add('filters-open');
    }
    function openAndScrollToSort() {
      open();
      requestAnimationFrame(function () {
        var sortSection = aside.querySelector('select[name="sort_by"]');
        if (sortSection) sortSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
    function close() {
      aside.classList.remove('is-open');
      document.body.classList.remove('filters-open');
    }
    openBtn.addEventListener('click', open);
    if (openSortBtn) openSortBtn.addEventListener('click', openAndScrollToSort);
    if (closeBtn) closeBtn.addEventListener('click', close);
    aside.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (link && window.matchMedia('(max-width: 767px)').matches) close();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* =====================================================================
   Bundle-add: "Complete Japandi look toevoegen (-10%)" — multi-add-to-cart
   met discount-code redirect.
   ===================================================================== */
(function () {
  function bindBundleAdd() {
    document.querySelectorAll('[data-bundle-add]').forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', async function () {
        const handles = btn.dataset.bundleHandles.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (handles.length === 0) return;
        const originalHTML = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = 'Bezig met toevoegen…';
        try {
          // Pak voor elke handle de eerste BESCHIKBARE variant
          const items = await Promise.all(handles.map(async function (h) {
            const r = await fetch('/products/' + h + '.js');
            if (!r.ok) throw new Error('Product ' + h + ' niet gevonden');
            const d = await r.json();
            const v = (d.variants || []).find(function (x) { return x.available; }) || (d.variants && d.variants[0]);
            if (!v) throw new Error('Geen variant voor ' + h);
            return { id: v.id, quantity: 1 };
          }));
          // Multi-add via /cart/add.js — Shopify ondersteunt items[]-array
          const addR = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
          if (!addR.ok) {
            const err = await addR.json().catch(function () { return {}; });
            throw new Error(err.description || 'Add-to-cart faalde');
          }
          // Apply discount + redirect naar cart
          window.location.href = '/discount/JAPANDISET10?redirect=/cart';
        } catch (e) {
          console.error('Bundle-add error:', e);
          btn.disabled = false;
          btn.innerHTML = '⚠ Probeer opnieuw';
          setTimeout(function () { btn.innerHTML = originalHTML; }, 3000);
        }
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindBundleAdd);
  } else {
    bindBundleAdd();
  }
})();

/* ---------------------------------------------------------------------
   Product-card media background
   Removes the visible "frame within a frame" by matching the card canvas
   to a reliable, uniform background colour sampled from the image edges.
   --------------------------------------------------------------------- */
(function () {
  var MEDIA_SELECTOR = '[data-product-card-media], [data-image-background]';
  var SAMPLE_SIZE = 32;

  function colourDistance(a, b) {
    var red = a[0] - b[0];
    var green = a[1] - b[1];
    var blue = a[2] - b[2];
    return Math.sqrt((red * red) + (green * green) + (blue * blue));
  }

  function sampleEdgeColour(img) {
    var canvas = document.createElement('canvas');
    var context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    context.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

    var pixels = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data;
    var samples = [];
    var inset = 2;

    function addPixel(x, y) {
      var offset = ((y * SAMPLE_SIZE) + x) * 4;
      if (pixels[offset + 3] < 230) return;
      samples.push([pixels[offset], pixels[offset + 1], pixels[offset + 2]]);
    }

    for (var i = 0; i < SAMPLE_SIZE; i += 1) {
      addPixel(i, inset);
      addPixel(i, SAMPLE_SIZE - 1 - inset);
      if (i > inset && i < SAMPLE_SIZE - 1 - inset) {
        addPixel(inset, i);
        addPixel(SAMPLE_SIZE - 1 - inset, i);
      }
    }

    if (samples.length < 24) return null;

    var buckets = {};
    samples.forEach(function (rgb) {
      var key = (rgb[0] >> 4) + '-' + (rgb[1] >> 4) + '-' + (rgb[2] >> 4);
      if (!buckets[key]) buckets[key] = { count: 0, red: 0, green: 0, blue: 0, colours: [] };
      buckets[key].count += 1;
      buckets[key].red += rgb[0];
      buckets[key].green += rgb[1];
      buckets[key].blue += rgb[2];
      buckets[key].colours.push(rgb);
    });

    var dominant = Object.keys(buckets).map(function (key) {
      return buckets[key];
    }).sort(function (a, b) {
      return b.count - a.count;
    })[0];

    if (!dominant || dominant.count / samples.length < 0.42) return null;

    var average = [
      Math.round(dominant.red / dominant.count),
      Math.round(dominant.green / dominant.count),
      Math.round(dominant.blue / dominant.count)
    ];
    var maximumDeviation = dominant.colours.reduce(function (max, colour) {
      return Math.max(max, colourDistance(colour, average));
    }, 0);

    if (maximumDeviation > 36) return null;
    if (average[0] > 244 && average[1] > 244 && average[2] > 244) return [255, 255, 255];
    return average;
  }

  function applyMediaBackground(media) {
    if (!media || media.dataset.backgroundSampled === 'true') return;
    var img = media.querySelector('img');
    if (!img) return;

    function sample() {
      if (!img.naturalWidth || !img.naturalHeight) return;
      try {
        var colour = sampleEdgeColour(img);
        if (colour) {
          media.style.setProperty('--product-card-image-bg', 'rgb(' + colour.join(', ') + ')');
        }
      } catch (error) {
        // Shopify CDN images normally allow canvas sampling. White remains the safe fallback.
      }
      media.dataset.backgroundSampled = 'true';
    }

    if (img.complete) sample();
    else img.addEventListener('load', sample, { once: true });
  }

  function scanProductCards(root) {
    if (root.matches && root.matches(MEDIA_SELECTOR)) applyMediaBackground(root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll(MEDIA_SELECTOR).forEach(applyMediaBackground);
  }

  function init() {
    scanProductCards(document);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) scanProductCards(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
// sanity-check-1778486542
